/**
 * Settings modal: dropdown-style panel positioned near the gear button.
 * Two-column layout: main menu (left) and options (right).
 * Categories: Theme, Language, Sound Theme, Orderbook Preferences.
 * Selecting an option slides back to the main menu.
 */
import React, { useCallback, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./SettingsModal.css";

type SubmenuKey = "theme" | "language" | "sound" | "layout" | "orderbook" | null;

export type ColorTheme = "theme1" | "theme2" | "theme3";
export type Language = "en" | "ko" | "zh" | "tr" | "es";
export type SoundStyle = "style1" | "style2" | "style3";
export type LayoutStyle = "layout1" | "layout2" | "layout3" | "layout4" | "layout5";

export interface OrderbookPrefs {
  animation: boolean;
  orderFillSound: boolean;
  orderFillNotification: boolean;
  setSizeOnClick: boolean;
  setSizeOnClickSound: boolean;
}

export interface SettingsModalState {
  colorTheme: ColorTheme;
  language: Language;
  soundStyle: SoundStyle;
  layout: LayoutStyle;
  orderbook: OrderbookPrefs;
}

const DEFAULT_ORDERBOOK: OrderbookPrefs = {
  animation: true,
  orderFillSound: true,
  orderFillNotification: true,
  setSizeOnClick: true,
  setSizeOnClickSound: false,
};

export const DEFAULT_SETTINGS: SettingsModalState = {
  colorTheme: "theme1",
  language: "en",
  soundStyle: "style1",
  layout: "layout1",
  orderbook: DEFAULT_ORDERBOOK,
};

const COLOR_THEMES: { value: ColorTheme; label: string }[] = [
  { value: "theme1", label: "Color Theme 1" },
  { value: "theme2", label: "Color Theme 2" },
  { value: "theme3", label: "Color Theme 3" },
];

const LANGUAGES: { value: Language; label: string }[] = [
  { value: "en", label: "English" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Chinese" },
  { value: "tr", label: "Turkish" },
  { value: "es", label: "Spanish" },
];

const SOUND_STYLES: { value: SoundStyle; label: string }[] = [
  { value: "style1", label: "Style 1" },
  { value: "style2", label: "Style 2" },
  { value: "style3", label: "Style 3" },
];

const LAYOUT_OPTIONS: { value: LayoutStyle; label: string }[] = [
  { value: "layout1", label: "Classic" },
  { value: "layout2", label: "Compact" },
  { value: "layout3", label: "Wide" },
  { value: "layout4", label: "Split" },
  { value: "layout5", label: "Grid" },
];

export interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: SettingsModalState;
  onStateChange: (state: SettingsModalState) => void;
  /** Optional: anchor element for positioning. If not provided, uses fixed top-right below menubar. */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

function SettingsModal({
  isOpen,
  onClose,
  state,
  onStateChange,
  anchorRef: _anchorRef,
}: SettingsModalProps): JSX.Element | null {
  const [activeSubmenu, setActiveSubmenu] = useState<SubmenuKey>(null);

  useEffect(() => {
    if (!isOpen) setActiveSubmenu(null);
  }, [isOpen]);

  const updateOrderbook = useCallback(
    (key: keyof OrderbookPrefs, value: boolean) => {
      onStateChange({
        ...state,
        orderbook: { ...state.orderbook, [key]: value },
      });
    },
    [state, onStateChange]
  );

  const selectTheme = useCallback(
    (value: ColorTheme) => {
      onStateChange({ ...state, colorTheme: value });
      setActiveSubmenu(null);
    },
    [state, onStateChange]
  );

  const selectLanguage = useCallback(
    (value: Language) => {
      onStateChange({ ...state, language: value });
      setActiveSubmenu(null);
    },
    [state, onStateChange]
  );

  const selectSoundStyle = useCallback(
    (value: SoundStyle) => {
      onStateChange({ ...state, soundStyle: value });
      setActiveSubmenu(null);
    },
    [state, onStateChange]
  );

  const selectLayout = useCallback(
    (value: LayoutStyle) => {
      onStateChange({ ...state, layout: value });
      setActiveSubmenu(null);
    },
    [state, onStateChange]
  );

  if (!isOpen) return null;

  const modalContent = (
    <div className="settings-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="settings-modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Settings"
      >
        <div className="settings-modal-header">
          <h3>Settings</h3>
          <button
            type="button"
            className="settings-modal-close"
            onClick={onClose}
            aria-label="Close settings"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className={`settings-modal-body settings-modal-two-column ${activeSubmenu ? "has-submenu" : ""}`}
        >
          {/* Column 1: Main menu */}
          <div className="settings-menu-column">
            <button
              type="button"
              className={`settings-menu-item ${activeSubmenu === "theme" ? "active" : ""}`}
              onClick={() => setActiveSubmenu(activeSubmenu === "theme" ? null : "theme")}
            >
              Theme
            </button>
            <button
              type="button"
              className={`settings-menu-item ${activeSubmenu === "language" ? "active" : ""}`}
              onClick={() => setActiveSubmenu(activeSubmenu === "language" ? null : "language")}
            >
              Language
            </button>
            <button
              type="button"
              className={`settings-menu-item ${activeSubmenu === "sound" ? "active" : ""}`}
              onClick={() => setActiveSubmenu(activeSubmenu === "sound" ? null : "sound")}
            >
              Sound Theme Preference
            </button>
            <button
              type="button"
              className={`settings-menu-item ${activeSubmenu === "layout" ? "active" : ""}`}
              onClick={() => setActiveSubmenu(activeSubmenu === "layout" ? null : "layout")}
            >
              Layout
            </button>
            <button
              type="button"
              className={`settings-menu-item ${activeSubmenu === "orderbook" ? "active" : ""}`}
              onClick={() => setActiveSubmenu(activeSubmenu === "orderbook" ? null : "orderbook")}
            >
              Orderbook Preferences
            </button>
          </div>

          {/* Column 2: Options (slides in when submenu selected) */}
          <div
            className={`settings-options-column ${activeSubmenu ? "visible" : ""}`}
            data-submenu={activeSubmenu}
          >
            {activeSubmenu === "theme" && (
              <div className="settings-options-panel">
                <h4 className="settings-options-panel-title">Theme</h4>
                <div className="settings-options">
                  {COLOR_THEMES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`settings-option ${state.colorTheme === value ? "active" : ""}`}
                      onClick={() => selectTheme(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeSubmenu === "language" && (
              <div className="settings-options-panel">
                <h4 className="settings-options-panel-title">Language</h4>
                <div className="settings-options">
                  {LANGUAGES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`settings-option ${state.language === value ? "active" : ""}`}
                      onClick={() => selectLanguage(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeSubmenu === "sound" && (
              <div className="settings-options-panel">
                <h4 className="settings-options-panel-title">Sound Theme Preference</h4>
                <div className="settings-options">
                  {SOUND_STYLES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`settings-option ${state.soundStyle === value ? "active" : ""}`}
                      onClick={() => selectSoundStyle(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeSubmenu === "layout" && (
              <div className="settings-options-panel">
                <h4 className="settings-options-panel-title">Layout</h4>
                <div className="settings-options settings-options-layout">
                  {LAYOUT_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`settings-option settings-option-58 ${state.layout === value ? "active" : ""}`}
                      onClick={() => selectLayout(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeSubmenu === "orderbook" && (
              <div className="settings-options-panel">
                <h4 className="settings-options-panel-title">Orderbook Preferences</h4>
                <div className="settings-toggles">
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={state.orderbook.animation}
                      onChange={(e) => updateOrderbook("animation", e.target.checked)}
                    />
                    <span>Animation</span>
                  </label>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={state.orderbook.orderFillSound}
                      onChange={(e) =>
                        updateOrderbook("orderFillSound", e.target.checked)
                      }
                    />
                    <span>Order Fill Sound</span>
                  </label>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={state.orderbook.orderFillNotification}
                      onChange={(e) =>
                        updateOrderbook("orderFillNotification", e.target.checked)
                      }
                    />
                    <span>Order Fill Notification (Background)</span>
                  </label>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={state.orderbook.setSizeOnClick}
                      onChange={(e) =>
                        updateOrderbook("setSizeOnClick", e.target.checked)
                      }
                    />
                    <span>Order Book Set Size on Click</span>
                  </label>
                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={state.orderbook.setSizeOnClickSound}
                      onChange={(e) =>
                        updateOrderbook("setSizeOnClickSound", e.target.checked)
                      }
                    />
                    <span>Order Book Set Size on Click Sound</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="settings-modal-footer">
          <span className="settings-status-badge">Online</span>
          <span className="settings-version">
            {new Date().toLocaleDateString()}.006a0e1
          </span>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}

export default SettingsModal;
